import axios from "axios"

import EnvVars from "~/env-vars"

import axiosInstance from "../api.axiosInstance"
import type {
  PublicSolarPresentationDto,
  SolarShareStateDto,
  SolarShareViewsDto,
} from "../dto/solar-presentation.dto"
import { withReadableErrorBody } from "../readable-error-body"

const sharePath = (proposalId: string): string =>
  `/solar-proposal/v2/${proposalId}/share`

/**
 * Recipient requests carry no Vision JWT: `axiosInstance` would attach a
 * logged-in seller's, and the public read must not be an authenticated one.
 */
const publicUrl = (path: string): string => `${EnvVars.API_URL}${path}`

/** Bare axios has no timeout of its own. Matches `axiosInstance`. */
const PUBLIC_READ_TIMEOUT_MS = 30_000

/** Past the gateway's 120s render budget, so the server's answer wins. */
const EXPORT_TIMEOUT_MS = 150_000

export class SolarPresentationService {
  async getShareState(proposalId: string): Promise<SolarShareStateDto> {
    const response = await axiosInstance.get<SolarShareStateDto>(
      sharePath(proposalId),
    )
    return response.data
  }

  /** Publishing is bodyless; the server owns readiness and the token. */
  async publish(proposalId: string): Promise<SolarShareStateDto> {
    const response = await axiosInstance.post<SolarShareStateDto>(
      sharePath(proposalId),
    )
    return response.data
  }

  async unpublish(proposalId: string): Promise<SolarShareStateDto> {
    const response = await axiosInstance.delete<SolarShareStateDto>(
      sharePath(proposalId),
    )
    return response.data
  }

  async getViews(proposalId: string): Promise<SolarShareViewsDto> {
    const response = await axiosInstance.get<SolarShareViewsDto>(
      `${sharePath(proposalId)}/views`,
    )
    return response.data
  }

  /**
   * The presentation as one continuous PDF page, the savings dial on `years`;
   * axios drops an unset `years`, leaving the dial on the page's default.
   */
  async exportPdf(proposalId: string, years?: number): Promise<Blob> {
    try {
      // No body, not even `null`, which axios would send as JSON "null".
      const response = await axiosInstance.post<ArrayBuffer>(
        `${sharePath(proposalId)}/export`,
        undefined,
        {
          params: { years },
          responseType: `arraybuffer`,
          timeout: EXPORT_TIMEOUT_MS,
        },
      )
      return new Blob([response.data], { type: `application/pdf` })
    } catch (error) {
      throw await withReadableErrorBody(error)
    }
  }

  async readPublic(token: string): Promise<PublicSolarPresentationDto> {
    const response = await axios.get<PublicSolarPresentationDto>(
      publicUrl(
        `/solar-proposal/v2/public-solar-presentations/${encodeURIComponent(token)}`,
      ),
      { timeout: PUBLIC_READ_TIMEOUT_MS },
    )
    return response.data
  }

  /** The presentation for the print page Chromium renders; the token is the authority. */
  async readForExport(token: string): Promise<PublicSolarPresentationDto> {
    const response = await axios.get<PublicSolarPresentationDto>(
      publicUrl(
        `/solar-proposal/v2/public-solar-presentations/export/${encodeURIComponent(token)}`,
      ),
      { timeout: PUBLIC_READ_TIMEOUT_MS },
    )
    return response.data
  }
}

export const solarPresentationService = new SolarPresentationService()
